/**
 * Proposals — `/proposals` library route (Phase G4A CP2).
 *
 * Displays the current user's proposal drafts in a card grid,
 * filterable by status (or "All" = default view which hides
 * archived). Each card carries quick actions (Continue / Duplicate /
 * Archive / Delete) via `ProposalCard`.
 *
 * Free tier: backend returns HTTP 402 `proposals_premium_only` on
 * every endpoint under `/api/proposals/*`. We catch that on the
 * initial list fetch and render the shared `PremiumUpgradePanel`
 * with `scope="proposals"` inline (no modal, no redirect — matches
 * G2A / G2B / G3A pattern).
 *
 * "Start a new proposal" CTA routes to `/proposals/new` for grant
 * selection before creating the draft row.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileEdit, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import AppNav from "@/components/app/AppNav";
import PremiumUpgradePanel from "@/components/grants/PremiumUpgradePanel";
import ProposalCard from "@/components/proposals/ProposalCard";
import FeatureDisabled from "@/feature-flags/FeatureDisabled";
import { PageHeader } from "@/design-system/components/PageHeader";
import { api, formatApiError } from "@/api/client";

const FILTERS = [
  { key: "active", label: "Active", statusParam: null },
  { key: "draft", label: "Draft", statusParam: "draft" },
  { key: "generated", label: "Generated", statusParam: "generated" },
  { key: "reviewed", label: "Reviewed", statusParam: "reviewed" },
  { key: "exported", label: "Exported", statusParam: "exported" },
  { key: "archived", label: "Archived", statusParam: "archived" },
];

export default function Proposals() {
  const [filter, setFilter] = useState("active");
  const [state, setState] = useState({
    loading: true,
    items: [],
    total: 0,
    error: null,
    isPremiumUpsell: false,
    isFeatureDisabled: false,
  });

  const currentFilter = useMemo(
    () => FILTERS.find((f) => f.key === filter) || FILTERS[0],
    [filter],
  );

  const fetchList = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const params = { limit: 100 };
      if (currentFilter.statusParam) {
        params.status = currentFilter.statusParam;
      }
      const res = await api.get("/proposals", { params });
      setState({
        loading: false,
        items: res.data?.items || [],
        total: res.data?.total || 0,
        error: null,
        isPremiumUpsell: false,
        isFeatureDisabled: false,
      });
    } catch (err) {
      // Discriminate response by `code` (NOT HTTP status alone) —
      // Free-tier premium 402 and admin-disabled 404 both live under
      // `/api/proposals/*` and require different UX. G4B CP2 rerun
      // P0 fix.
      const code = err?.response?.data?.detail?.code;
      if (code === "proposals_premium_only") {
        setState({
          loading: false,
          items: [],
          total: 0,
          error: null,
          isPremiumUpsell: true,
          isFeatureDisabled: false,
        });
        return;
      }
      if (code === "feature_disabled") {
        setState({
          loading: false,
          items: [],
          total: 0,
          error: null,
          isPremiumUpsell: false,
          isFeatureDisabled: true,
        });
        return;
      }
      setState({
        loading: false,
        items: [],
        total: 0,
        error: formatApiError(err),
        isPremiumUpsell: false,
        isFeatureDisabled: false,
      });
    }
  }, [currentFilter]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  async function handleDuplicate(proposal) {
    try {
      const res = await api.post(`/proposals/${proposal.id}/duplicate`);
      toast.success(`Duplicated to "${res.data?.title || "new draft"}"`);
      fetchList();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function handleArchive(proposal) {
    try {
      await api.post(`/proposals/${proposal.id}/archive`);
      toast.success("Proposal archived");
      fetchList();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function handleDelete(proposal) {
    try {
      await api.delete(`/proposals/${proposal.id}`);
      toast.success("Draft deleted");
      fetchList();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  return (
    <main className="min-h-screen text-[var(--ds-color-ink)] bg-[var(--ds-color-background)] pb-20">
      <AppNav />
      <div className="mx-auto max-w-6xl px-5 md:px-8 pt-6">
        <PageHeader
          eyebrow="Grant Writer"
          title="Proposal Library"
          description="Draft grant proposals with a guided 10-step wizard. Save your work as you go, duplicate a proven proposal for a new grant, and keep every version organised in one place."
          data-testid="proposals-header"
          actions={
            <Link
              to="/proposals/new"
              data-testid="proposals-start-new-cta"
              className="inline-flex items-center gap-2 rounded-[var(--ds-radius-md)] px-4 py-2 text-[13px] font-semibold transition-colors"
              style={{
                background: "var(--ds-color-accent)",
                color: "var(--ds-color-accent-ink)",
                transitionDuration: "var(--ds-duration-fast)",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "var(--ds-color-accent-strong)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "var(--ds-color-accent)")
              }
            >
              <Plus className="w-4 h-4" />
              Start a new proposal
            </Link>
          }
        />

        {state.isFeatureDisabled ? (
          <FeatureDisabled
            testId="grant-writer-feature-disabled"
            title="Grant Writer is temporarily disabled"
            message="An administrator has paused the Grant Writer module. Your saved proposal drafts remain in the system and will re-appear when the feature is re-enabled."
          />
        ) : state.isPremiumUpsell ? (
          <PremiumUpgradePanel scope="proposals" />
        ) : (
          <>
            <nav
              data-testid="proposals-filter-tabs"
              className="mt-2 flex items-center gap-1 overflow-x-auto"
            >
              {FILTERS.map((f) => {
                const active = f.key === filter;
                return (
                  <button
                    key={f.key}
                    type="button"
                    data-testid={`proposals-filter-${f.key}`}
                    onClick={() => setFilter(f.key)}
                    className={`rounded-[var(--ds-radius-md)] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] whitespace-nowrap transition-colors border ${
                      active
                        ? "bg-[var(--ds-color-surface)] text-[var(--ds-color-ink)] border-[var(--ds-color-border-strong)]"
                        : "bg-transparent text-[var(--ds-color-ink-muted)] border-transparent hover:text-[var(--ds-color-ink)] hover:bg-[var(--ds-color-surface-muted)]"
                    }`}
                  >
                    {f.label}
                  </button>
                );
              })}
            </nav>

            {state.loading ? (
              <div
                data-testid="proposals-loading"
                className="mt-6 flex items-center gap-2 text-[13px] text-[var(--ds-color-ink-muted)]"
              >
                <Loader2 className="w-4 h-4 animate-spin" /> Loading proposals…
              </div>
            ) : state.error ? (
              <div
                data-testid="proposals-error"
                className="mt-6 rounded-[var(--ds-radius-card)] border-l-4 border p-4 text-[13px]"
                style={{
                  background: "var(--ds-color-danger-soft)",
                  borderColor: "var(--ds-color-danger)",
                  borderLeftColor: "var(--ds-color-danger)",
                  color: "var(--ds-color-danger)",
                }}
              >
                {state.error}
              </div>
            ) : state.items.length === 0 ? (
              <EmptyState filterKey={filter} />
            ) : (
              <div
                data-testid="proposals-grid"
                className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3"
              >
                {state.items.map((p) => (
                  <ProposalCard
                    key={p.id}
                    proposal={p}
                    onDuplicate={handleDuplicate}
                    onArchive={handleArchive}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}


function EmptyState({ filterKey }) {
  const isActive = filterKey === "active";
  return (
    <div
      data-testid="proposals-empty-state"
      className="mt-6 rounded-[var(--ds-radius-card)] border border-dashed border-[var(--ds-color-border-strong)] bg-[var(--ds-color-surface)] p-10 flex flex-col items-center text-center gap-4"
    >
      <div
        className="rounded-[var(--ds-radius-md)] p-3"
        style={{
          background: "var(--ds-color-accent-soft)",
        }}
      >
        <FileEdit
          className="w-5 h-5"
          style={{ color: "var(--ds-color-accent)" }}
          aria-hidden="true"
        />
      </div>
      <div className="max-w-lg">
        <h3
          className="text-[16px] font-semibold"
          style={{ color: "var(--ds-color-ink)" }}
        >
          {isActive ? "No proposals yet" : "Nothing in this view"}
        </h3>
        <p className="mt-2 text-[13px] text-[var(--ds-color-ink-muted)]">
          {isActive
            ? "Start a new proposal to capture your business overview, funding request, and project scope. You can save your draft at any time and pick up where you left off."
            : "Switch filters to see proposals in other lifecycle stages, or start a new draft."}
        </p>
      </div>
      {isActive && (
        <Link
          to="/proposals/new"
          data-testid="proposals-empty-cta"
          className="inline-flex items-center gap-2 rounded-[var(--ds-radius-md)] px-4 py-2 text-[13px] font-semibold text-[var(--ds-color-accent-ink)] bg-[var(--ds-color-accent)] hover:bg-[var(--ds-color-accent-strong)] transition-colors"
        >
          <Plus className="w-4 h-4" />
          Start a new proposal
        </Link>
      )}
    </div>
  );
}
